// ─── Pattern Interpretation Transformer ─────────────────────────────────────
//
// Builds the dashboard's PatternInterpretation section from a PhenotypeProfile.
// This is a concise "what pattern is currently most supported" view — distinct
// from the full /phenotype page. Shows:
//   - detected pattern (if any)
//   - supporting signals (up to 5)
//   - missing data points that would strengthen or refute the pattern
//   - alternative explanations the clinician should consider
//
// Uses the existing phenotypeInterpretations.tsx copy source for lens-aware
// strings so patient and clinician tone stays consistent with the /phenotype
// page.
// ──────────────────────────────────────────────────────────────────────────────

import type { PhenotypeProfile } from '../phenotype';
import {
  PHENOTYPE_INTERPRETATIONS,
  FALLBACK_INTERPRETATION,
} from '../phenotypeInterpretations';
import type {
  PatternInterpretation,
  ContributingSignal,
  MissingSignal,
  ConfidenceLevel,
  TrendDirection,
} from './types';
import { lensCopy } from './lensCopy';

// ─── Inputs ──────────────────────────────────────────────────────────────────

export interface PatternInterpretationInput {
  profile: PhenotypeProfile | null;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

/**
 * Strip parenthesized clinical screener abbreviations from a signal label
 * so the patient lens can render it directly. Matches `(PHQ-4)`, `(PHQ-2)`,
 * `(GAD-2)`, `(GAD-7)`, and any similar `(XXX-N)` pattern inside parens.
 * Also collapses the resulting double spaces so the label reads cleanly.
 *
 * Examples:
 *   "Anxiety Subscale (PHQ-4)"     → "Anxiety Subscale"
 *   "Mood Subscale (PHQ-2)"        → "Mood Subscale"
 *   "Generalized Anxiety (GAD-7)"  → "Generalized Anxiety"
 *   "Heart Rate Variability"       → "Heart Rate Variability"  (unchanged)
 */
export function sanitizeSignalLabel(label: string): string {
  return label
    .replace(/\s*\((?:PHQ|GAD)-?\d+\)/gi, '')
    .replace(/\s+/g, ' ')
    .trim();
}

/** Map phenotype confidence (0-100) + completeness to our ConfidenceLevel. */
function classifyPhenotypeConfidence(
  phenotypeConfidence: number,
  dataCompleteness: number,
): ConfidenceLevel {
  // Require both confidence and data completeness to be substantial for "high"
  if (phenotypeConfidence >= 70 && dataCompleteness >= 70) return 'high';
  if (phenotypeConfidence >= 50 && dataCompleteness >= 50) return 'moderate';
  if (phenotypeConfidence > 0 && dataCompleteness > 0) return 'low';
  return 'insufficient';
}

/**
 * Map phenotype signal direction to our TrendDirection vocabulary.
 *
 * Exported for direct test coverage of the full mapping table. The public
 * caller in this module filters out 'normal' and 'unknown' before calling,
 * so in production only 'elevated' and 'reduced' inputs are observed — the
 * test table locks the other two branches in case future callers (or
 * removal of the filter) start exercising them.
 */
export function mapSignalDirection(
  direction: 'elevated' | 'reduced' | 'normal' | 'unknown',
): TrendDirection {
  if (direction === 'elevated') return 'declining'; // "elevated inflammation" is a concern
  if (direction === 'reduced') return 'declining'; // "reduced HRV" is a concern
  if (direction === 'normal') return 'stable';
  return 'unknown';
}

// ─── Alternative explanations per phenotype ─────────────────────────────────

const ALTERNATIVE_EXPLANATIONS: Record<string, string[]> = {
  hpa_dominant: [
    'Acute psychosocial stressor (not chronic HPA dysregulation)',
    'Recent illness or infection',
    'Overtraining or inadequate recovery from exercise load',
    'Caffeine or stimulant load affecting autonomic balance',
  ],
  immunometabolic: [
    'Acute infection or transient inflammatory response',
    'Dietary quality shift in the past 2-4 weeks',
    'Medication side effects affecting metabolic markers',
    'Seasonal variation in activity and diet patterns',
  ],
  anhedonic: [
    'Acute stressor reducing motivation temporarily',
    'Sleep debt or circadian disruption blunting reward',
    'Pharmacologic effect (e.g., recent medication changes)',
    'Bereavement or major life transition',
  ],
  anxious: [
    'Acute stressor or upcoming life event',
    'Caffeine or stimulant sensitivity',
    'Sleep disruption contributing to physiological arousal',
    'Medication withdrawal or side effect',
  ],
};

// ─── Main transformer ────────────────────────────────────────────────────────

/**
 * Build the dashboard PatternInterpretation view model from a phenotype
 * profile. Gracefully handles null profile and the "no pattern detected"
 * case.
 */
export function computePatternInterpretation(
  input: PatternInterpretationInput,
): PatternInterpretation {
  const { profile } = input;

  // Case 1: No profile at all
  if (!profile) {
    return {
      detected: false,
      primary: null,
      summary: lensCopy(
        'Not enough recent data to detect a pattern yet.',
        'Insufficient data for phenotype grading.',
      ),
      supportingSignals: [],
      missingData: [],
      alternativeExplanations: [],
      confidence: 'insufficient',
    };
  }

  const primary = profile.primary;
  const dataCompleteness = profile.dataCompleteness;

  // Case 2: Profile exists but no primary pattern
  if (!primary) {
    return {
      detected: false,
      primary: null,
      summary: lensCopy(
        'No dominant pattern in your recent data.',
        'No phenotype met significance threshold; signals distributed across patterns.',
      ),
      supportingSignals: [],
      missingData: profile.missingData.map((label, i) => ({
        id: `missing:${i}`,
        label,
        howToAdd: 'Add this data source to strengthen interpretation',
      })),
      alternativeExplanations: [],
      confidence: 'insufficient',
    };
  }

  // Case 3: Primary pattern detected
  const interpretation =
    PHENOTYPE_INTERPRETATIONS[primary.id] ?? FALLBACK_INTERPRETATION;

  const supportingSignals: ContributingSignal[] = primary.signals
    .filter((s) => s.direction !== 'normal' && s.direction !== 'unknown')
    .slice(0, 5)
    .map((s, i) => {
      const sanitized = sanitizeSignalLabel(s.label);
      const wasChanged = sanitized !== s.label;
      return {
        id: `sig:${primary.id}:${s.key}:${i}`,
        label: sanitized,
        // Only set labelClinician when sanitization actually changed the
        // label — otherwise the two lenses would render the same string.
        labelClinician: wasChanged ? s.label : undefined,
        value: s.value != null ? String(s.value) : '—',
        direction: mapSignalDirection(s.direction),
        domain: primary.id,
      };
    });

  const missingData: MissingSignal[] = profile.missingData.map((label, i) => ({
    id: `missing:${primary.id}:${i}`,
    label,
    howToAdd: 'Add this data source to strengthen interpretation',
  }));

  const alternativeExplanations = ALTERNATIVE_EXPLANATIONS[primary.id] ?? [];

  return {
    detected: true,
    primary,
    summary: lensCopy(
      interpretation.whatThisSuggests,
      interpretation.careConversations,
    ),
    supportingSignals,
    missingData,
    alternativeExplanations,
    confidence: classifyPhenotypeConfidence(primary.confidence, dataCompleteness),
  };
}
